/*
 * Which layout of a screen the cloud page (/plants/:id/hmi) shows.
 *
 * The page used to know only desktop and phone portrait. A screen drawn only
 * for the TP8 (free, 1280 x 800 or 800 x 1280) then showed nothing but the
 * auto flow. Now every layout counts, in this order of preference:
 *
 *   phone in landscape: mobileLandscape, if the screen has one;
 *   narrow window:      mobileResponsive; else the desktop grid stacked in
 *                       one column (as before); else the portrait panel;
 *   otherwise:          desktopResponsive, panelFixed1280x800,
 *                       panelPortrait800x1280, panelFixed1024x600,
 *                       mobileLandscape, mobileResponsive.
 *
 * Pure module (only type imports), tested in tests/plantTarget.test.ts.
 */

import type { LayoutTarget, ScreenLayout } from './widgets';

export const PLANT_TARGET_ORDER: readonly LayoutTarget[] = [
  'desktopResponsive',
  'panelFixed1280x800',
  'panelPortrait800x1280',
  'panelFixed1024x600',
  'mobileLandscape',
  'mobileResponsive',
];

export interface PlantViewport {
  /** Window at most the phone breakpoint wide. */
  narrow: boolean;
  /** A phone held sideways: low and wider than high. */
  phoneLandscape: boolean;
}

export interface PlantTargetChoice {
  target: LayoutTarget;
  /** The layout to render, or null (no layout: auto flow, as before). */
  layout: ScreenLayout | null;
  /** Narrow window without a phone layout: desktop grid in one column. */
  stack: boolean;
}

export function pickPlantTarget(layouts: readonly ScreenLayout[], viewport: PlantViewport): PlantTargetChoice {
  const find = (t: LayoutTarget) => layouts.find((l) => l.target === t) ?? null;
  const choose = (t: LayoutTarget): PlantTargetChoice | null => {
    const layout = find(t);
    return layout ? { target: t, layout, stack: false } : null;
  };

  if (viewport.phoneLandscape) {
    const landscape = choose('mobileLandscape');
    if (landscape) return landscape;
  }

  if (viewport.narrow) {
    const mobile = choose('mobileResponsive');
    if (mobile) return mobile;
    const desktop = find('desktopResponsive');
    if (desktop && desktop.mode !== 'free') return { target: 'desktopResponsive', layout: desktop, stack: true };
    const portrait = choose('panelPortrait800x1280');
    if (portrait) return portrait;
  }

  for (const t of PLANT_TARGET_ORDER) {
    const hit = choose(t);
    if (hit) return hit;
  }
  return { target: 'desktopResponsive', layout: null, stack: viewport.narrow };
}
